import * as React from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

/**
 * SearchableSelect — combobox léger avec recherche instantanée.
 *
 * Utilisation typique pour listes potentiellement longues (collaborateurs,
 * sites, bénéficiaires) : l'utilisateur peut taper quelques lettres pour
 * filtrer, ce qui est plus rapide que scroller un Select classique.
 *
 * Props :
 *   - value          : string (valeur sélectionnée, ou "" si aucune)
 *   - onChange(v)    : appelée avec la nouvelle valeur (string). "" si effacé.
 *   - options        : Array<{ value: string, label: string, keywords?: string, hint?: string }>
 *                      `keywords` sert au filtrage (ex. poste, matricule).
 *                      `hint` s'affiche en gris à droite du label.
 *   - placeholder    : texte affiché quand value vide
 *   - emptyLabel     : texte affiché en absence de résultat de recherche
 *   - searchPlaceholder : placeholder du champ de recherche
 *   - allowClear     : ajoute un bouton × pour vider la sélection
 *   - disabled       : désactive le combobox
 *   - className      : classes additionnelles sur le déclencheur
 *   - data-testid    : posé sur le déclencheur ; sous-testids `-opt-{value}` sur chaque item
 */
const SearchableSelect = React.forwardRef(function SearchableSelect(
  {
    value,
    onChange,
    options,
    placeholder = "Sélectionner…",
    emptyLabel = "Aucun résultat.",
    searchPlaceholder = "Rechercher…",
    allowClear = false,
    disabled = false,
    className,
    // Action de création rapide en pied de popover. Si fourni, un bouton
    // « + <createNewLabel> » est rendu sous la liste : au clic, la popover
    // se ferme et le callback est déclenché (au parent d'ouvrir sa modale).
    onCreateNew,
    createNewLabel = "Créer",
    ...props
  },
  ref,
) {
  const testId = props["data-testid"];
  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const commit = (v) => {
    onChange && onChange(v);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange && onChange("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-between h-9 px-3 font-normal text-sm bg-white",
            !selected && "text-zinc-500",
            className,
          )}
        >
          <span className="truncate text-left">
            {selected ? selected.label : placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-2 text-zinc-400">
            {allowClear && selected ? (
              <span
                role="button"
                aria-label="Effacer"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={clear}
                className="hover:text-zinc-700 cursor-pointer"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <X size={14} />
              </span>
            ) : null}
            <ChevronsUpDown size={14} className="opacity-60" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]"
        align="start"
        sideOffset={4}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue vient du prop `value` du CommandItem (chaîne recherchable)
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const searchable = [opt.label, opt.keywords || "", opt.hint || ""]
                  .filter(Boolean)
                  .join(" ");
                const isSelected = value === opt.value;
                return (
                  <CommandItem
                    key={opt.value}
                    value={searchable}
                    onSelect={() => commit(opt.value)}
                    data-testid={testId ? `${testId}-opt-${opt.value}` : undefined}
                  >
                    <Check
                      size={14}
                      className={cn(
                        "mr-2",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.hint ? (
                      <span className="ml-2 text-xs text-zinc-500 truncate max-w-[40%]">
                        {opt.hint}
                      </span>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {onCreateNew ? (
            <div className="border-t border-zinc-200 p-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onCreateNew();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-emerald-700 hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none transition-colors"
                data-testid={testId ? `${testId}-create-new` : undefined}
              >
                <Plus size={14} />
                <span className="font-medium">{createNewLabel}</span>
              </button>
            </div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
});

export default SearchableSelect;
